/** Official Starlink market status + usage-proxy payload for the product view. */

import fallback from '@/data/valueChain/starlinkMarkets.json';
import { COMTRADE_REGION_ORDER } from '@/components/value-chain/starlinkComtrade';

export type MarketBucket = 'live' | 'coming_soon' | 'pending' | 'blocked' | 'unknown';

export type StarlinkMarket = {
  iso2: string;
  iso3: string | null;
  name: string;
  region: string;
  status: string;
  status_bucket: MarketBucket;
  expected: string;
  debut: string | null;
  debut_source: string | null;
  download_p50: number | null;
  upload_p50: number | null;
  latency_p50: number | null;
  ookla_rank: number | null;
  ookla_sample_share: number | null;
  printed_subscribers_m: number | null;
  labeled_kits: boolean;
  source_url: string | null;
  fetched_at?: string;
  notes: string;
};

export type StarlinkMarketsPayload = {
  source: string;
  fetched_at: string;
  kpis: {
    live: number;
    coming_soon: number;
    pending: number;
    blocked: number;
    unknown: number;
    metrics_countries: number;
    debut_countries: number;
  };
  printed: {
    subscribers_m: number;
    subscribers_as_of: string;
    subscribers_source: string;
    markets_mda: number;
    arpu_usd: number;
  };
  ookla: {
    period: string;
    source: string;
    caveat: string;
    markets: Array<{
      iso2: string;
      iso3: string | null;
      name: string;
      rank: number | null;
      sample_share: number | null;
      printed_subscribers_m: number | null;
      note: string;
    }>;
  };
  markets: StarlinkMarket[];
  caveat: string;
};

export type MarketTile = {
  id: string;
  iso2: string | null;
  label: string;
  weight: number;
  bucket: MarketBucket;
  status: string;
  ooklaShare: number | null;
  labeled: boolean;
};

export type MarketRegionGroup = {
  id: string;
  label: string;
  count: number;
  tiles: MarketTile[];
};

export const MARKETS_FALLBACK = fallback as StarlinkMarketsPayload;

export const BUCKET_LABEL: Record<MarketBucket, string> = {
  live: 'Live',
  coming_soon: 'Coming soon',
  pending: 'Pending',
  blocked: 'Blocked',
  unknown: 'Unknown',
};

const TILES_PER_REGION = 7;
const PINNED_ISO2 = new Set(['US', 'MX', 'ID', 'BR', 'CA', 'EC', 'GH', 'LS', 'NG']);

export function marketWeight(market: StarlinkMarket): number {
  if (market.ookla_sample_share && market.ookla_sample_share > 0) {
    return market.ookla_sample_share * 1000;
  }
  if (market.printed_subscribers_m && market.printed_subscribers_m > 0) {
    return market.printed_subscribers_m * 40;
  }
  if (market.labeled_kits) return 8;
  if (market.ookla_rank) return 12;
  return 1;
}

export function filterMarkets(
  payload: StarlinkMarketsPayload,
  bucket: MarketBucket | 'all',
): StarlinkMarket[] {
  if (bucket === 'all') {
    return payload.markets.filter((market) => market.status_bucket !== 'unknown');
  }
  return payload.markets.filter((market) => market.status_bucket === bucket);
}

export function groupMarketRegions(
  payload: StarlinkMarketsPayload,
  bucket: MarketBucket | 'all',
): MarketRegionGroup[] {
  const members = filterMarkets(payload, bucket);
  const buckets = new Map<string, StarlinkMarket[]>();
  members.forEach((market) => {
    const region = market.region || 'Other';
    const list = buckets.get(region) ?? [];
    list.push(market);
    buckets.set(region, list);
  });

  return COMTRADE_REGION_ORDER.flatMap((region) => {
    const list = (buckets.get(region) ?? []).sort((a, b) => {
      const weight = marketWeight(b) - marketWeight(a);
      if (weight !== 0) return weight;
      return a.name.localeCompare(b.name);
    });
    if (!list.length) return [];
    const pinned = list.filter((market) => PINNED_ISO2.has(market.iso2));
    const rest = list.filter((market) => !PINNED_ISO2.has(market.iso2));
    const keep = new Set(
      [...pinned, ...rest.slice(0, Math.max(0, TILES_PER_REGION - pinned.length))].map(
        (market) => market.iso2,
      ),
    );
    const head = list.filter((market) => keep.has(market.iso2));
    const overflow = list.filter((market) => !keep.has(market.iso2));
    const tiles: MarketTile[] = head.map((market) => ({
      id: market.iso2,
      iso2: market.iso2,
      label: shortMarketName(market.name),
      weight: marketWeight(market),
      bucket: market.status_bucket,
      status: market.status,
      ooklaShare: market.ookla_sample_share,
      labeled: market.labeled_kits,
    }));
    if (overflow.length) {
      tiles.push({
        id: `${region}-other`,
        iso2: null,
        label: `Other (${overflow.length})`,
        weight: overflow.length,
        bucket: overflow[0]?.status_bucket ?? 'unknown',
        status: overflow[0]?.status ?? '',
        ooklaShare: null,
        labeled: overflow.some((item) => item.labeled_kits),
      });
    }
    return [{ id: region, label: region, count: list.length, tiles }];
  });
}

export function shortMarketName(name: string): string {
  return name
    .replace('United States', 'United States')
    .replace('United Kingdom', 'UK')
    .replace('United Arab Emirates', 'UAE')
    .replace('Dominican Republic', 'Dominican Rep.')
    .replace('Central African Republic', 'CAR')
    .replace('Bosnia and Herzegovina', 'Bosnia')
    .replace('Saint Vincent and the Grenadines', 'St. Vincent')
    .replace('Antigua and Barbuda', 'Antigua')
    .replace('Trinidad and Tobago', 'Trinidad')
    .replace('São Tomé and Príncipe', 'São Tomé')
    .replace('Northern Mariana Islands', 'N. Mariana')
    .replace('Saint Pierre and Miquelon', 'St. Pierre');
}

export function formatShare(share: number | null | undefined): string {
  if (share == null || !Number.isFinite(share)) return '—';
  const pct = share * 100;
  return pct >= 10 ? `${pct.toFixed(0)}%` : `${pct.toFixed(1)}%`;
}

export function formatMbps(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${Math.round(value)} Mbps`;
}

export function formatMs(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${Math.round(value)} ms`;
}

export function formatDebut(value: string | null | undefined): string {
  if (!value) return '—';
  const [year, month] = value.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const idx = Number(month) - 1;
  if (!year || Number.isNaN(idx) || !months[idx]) return value;
  return `${months[idx]} ${year}`;
}
